import { useState } from "react";

import { Loader2, Send, Sparkles } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";

type PrebuiltPersona = {
  role_name: string;
  mission: string;
  principles: string;
  competencies: string;
  key_relationships: string;
  typical_questions: string;
};

type Message = {
  role: "user" | "assistant";
  content: string;
  persona?: string;
};

interface BoardPreviewProps {
  board: {
    name: string;
    description: string;
    personas: PrebuiltPersona[];
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BoardPreview = ({ board, open, onOpenChange }: BoardPreviewProps) => {
  const { execute: invoke, isLoading } = useEdgeFunction({ auth: true });
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: `Welcome to the ${board.name} preview! I'm here to help you test the advisory board. Ask me anything and I'll respond as the most relevant expert from the board.`,
    },
  ]);
  const [input, setInput] = useState("");

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    // Build context for AI with all personas
    const personasContext = board.personas
      .map(
        (p) =>
          `${p.role_name}:
Mission: ${p.mission}
Principles: ${p.principles}
Competencies: ${p.competencies}
Key Questions: ${p.typical_questions}`
      )
      .join("\n\n");

    const systemPrompt = `You are simulating an advisory board called "${board.name}". 
${board.description}

Available advisors:
${personasContext}

When responding:
1. Select the most relevant advisor for the question
2. Start your response with "[Advisor Name]:" 
3. Answer in character with their expertise and perspective
4. Keep responses concise and actionable (2-3 paragraphs)
5. Reference their principles and competencies naturally`;

    const { data, error } = await invoke("chat", {
      body: {
        messages: [
          { role: "system", content: systemPrompt },
          ...messages.map((m) => ({ role: m.role, content: m.content })),
          { role: "user", content: userMessage },
        ],
      },
      showErrorToast: true,
    });

    if (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
      return;
    }

    // Extract persona name if response starts with [Name]:
    const responseText = data.response || "I apologize, but I couldn't generate a response.";
    const personaMatch = responseText.match(/^\[(.+?)\]:/);
    const personaName = personaMatch ? personaMatch[1] : undefined;
    const cleanedResponse = personaName
      ? responseText.replace(/^\[.+?\]:\s*/, "")
      : responseText;

    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: cleanedResponse,
        persona: personaName,
      },
    ]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Preview: {board.name}
          </DialogTitle>
          <DialogDescription>
            Test the board personas in action before publishing
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            {messages.map((message, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {message.role === "assistant" && (
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {message.persona
                        ? message.persona
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                        : "AI"}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div
                  className={`rounded-lg px-4 py-2 max-w-[80%] ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  {message.persona && (
                    <p className="text-xs font-semibold mb-1 opacity-70">
                      {message.persona}
                    </p>
                  )}
                  {message.role === "assistant" ? (
                    <Markdown content={message.content} />
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </AvatarFallback>
                </Avatar>
                <div className="rounded-lg px-4 py-2 bg-muted">
                  <p className="text-sm text-muted-foreground">Thinking...</p>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="flex gap-2 pt-4 border-t">
          <Input
            placeholder="Ask a question to test the board..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <Button onClick={handleSend} disabled={isLoading || !input.trim()}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
